import * as net from 'util/Net';

import {InAppNotificationStatus} from './InAppNotificationConstants';

// Keep in sync with /src/benchling/notifications/network/rest/in_app_notification_rest_api.py
const IN_APP_NOTIFICATIONS_URL = '/1/api/in-app-notifications';

// Matches MAX_UPDATES_PER_REQUEST on the endpoint; requests carrying more updates are rejected. Keep in
// sync with /src/benchling/notifications/network/rest/in_app_notification_rest_api.py.
const MAX_UPDATES_PER_REQUEST = 100;

interface InAppNotificationUpdate {
  id: string;
  status: InAppNotificationStatus;
}

interface UpdateInAppNotificationsResponse {
  updatedIds: Array<string>;
}

/**
 * Bulk-update the acting user's notifications. The endpoint is bulk so "mark all as read" is one
 * request; the connector 404s ids owned by anyone else, so there is no recipient parameter.
 */
export function updateInAppNotifications(
  updates: Array<InAppNotificationUpdate>
): Promise<UpdateInAppNotificationsResponse> {
  return net.patchJSONPromise({
    url: IN_APP_NOTIFICATIONS_URL,
    data: {updates},
  });
}

/** Mark a single notification read. */
export function markNotificationRead(id: string): Promise<UpdateInAppNotificationsResponse> {
  return updateInAppNotifications([{id, status: InAppNotificationStatus.READ}]);
}

/**
 * Mark several notifications read (the "Mark all read" affordance). The endpoint caps each request at
 * `MAX_UPDATES_PER_REQUEST`, so the ids are split into that many per request and the batches run in
 * parallel; the resolved `updatedIds` are concatenated back into a single response.
 *
 * This is an interim stop-gap: BNCH-172717 tracks a high-water-mark "mark read up to timestamp"
 * endpoint that will let the client mark every unread notification in one bounded request instead.
 */
export async function markNotificationsRead(ids: Array<string>): Promise<UpdateInAppNotificationsResponse> {
  const batches: Array<Array<string>> = [];
  for (let i = 0; i < ids.length; i += MAX_UPDATES_PER_REQUEST) {
    batches.push(ids.slice(i, i + MAX_UPDATES_PER_REQUEST));
  }
  const responses = await Promise.all(
    batches.map((batch) =>
      updateInAppNotifications(batch.map((id) => ({id, status: InAppNotificationStatus.READ})))
    )
  );
  return {updatedIds: responses.flatMap((response) => response.updatedIds)};
}

/** Remove a notification from the list. Dismiss is a hard removal — no undo, no confirmation. */
export function dismissNotification(id: string): Promise<UpdateInAppNotificationsResponse> {
  return updateInAppNotifications([{id, status: InAppNotificationStatus.DISMISSED}]);
}
